/**
 * Recop Design Tokens
 * Based on Figma design system
 */

// Base colors from Figma
export const baseColors = {
  bg: '#0f1012', // base-bg
  segBg: '#16171b', // seg-bg
  selectedBg: '#222328', // selected-bg
  border: '#1e1f24', // border
} as const;

// Typography colors from Figma
export const fontColors = {
  primary: '#e6e6e9', // font-primary
  secondary: '#b3b5bd', // font-secondary
  muted: '#8f9099', // font-muted
  lowkey: '#4f5055', // font-lowkey
} as const;

// Accent colors from Figma
export const accentColors = {
  primary: '#5e69d3', // accent-primary
  primaryHover: '#4c5bc4', // accent-primary-hover
  orange: '#ffa852', // accent-orange
  red: '#BC2F3D', // accent-red
  green: '#07712B', // accent-green
  blue: '#258BFF', // accent-blue
} as const;

// Font sizes from Figma (in px, converted to rem in usage)
export const fontSizes = {
  '2xs': 10, // font-2xs
  xs: 11, // font-xs
  sm: 13, // font-sm
  md: 15, // font-md
  lg: 18, // font-lg
  xl: 21, // font-xl
} as const;

// Spacing from Figma (in px, converted to rem in usage)
export const spacing = {
  xxs: 2, // spacing-xxs
  xs: 5, // spacing-xs
  sm: 10, // spacing-sm
  md: 21, // spacing-md
  lg: 35, // spacing-lg
} as const;

// Border radius from Figma (in px, converted to rem in usage)
export const radii = {
  xs: 3, // radii-xs
  sm: 6, // radii-sm
  lg: 8, // radii-lg
  xl: 99, // radii-xl (pill)
} as const;

// Semantic color mappings for components
export const semanticColors = {
  // Backgrounds
  bg: {
    default: baseColors.bg,
    surface: baseColors.segBg,
    elevated: baseColors.selectedBg,
    hover: 'rgba(94, 105, 211, 0.1)',
    active: 'rgba(94, 105, 211, 0.2)',
    selected: baseColors.selectedBg,
    primary: accentColors.primary,
    orange: accentColors.orange,
    red: accentColors.red,
    green: accentColors.green,
    blue: accentColors.blue,
  },

  // Text
  text: {
    primary: fontColors.primary,
    secondary: fontColors.secondary,
    muted: fontColors.muted,
    lowkey: fontColors.lowkey,
    inverse: baseColors.bg,
    accent: accentColors.primary,
    orange: accentColors.orange,
  },

  // Borders
  border: {
    default: baseColors.border,
    subtle: 'rgba(30, 31, 36, 0.5)',
    accent: accentColors.primary,
    hover: 'rgba(94, 105, 211, 0.5)',
  },
} as const;

// Component-specific tokens
export const componentTokens = {
  sidebar: {
    bg: baseColors.segBg,
    selected: baseColors.selectedBg,
    hover: 'rgba(94, 105, 211, 0.1)',
  },

  card: {
    bg: baseColors.segBg,
    border: baseColors.border,
    hover: 'rgba(94, 105, 211, 0.05)',
  },

  button: {
    primary: {
      bg: accentColors.primary,
      hover: '#4a56cc',
      active: '#3d47b8',
      text: fontColors.primary,
    },
    secondary: {
      bg: baseColors.segBg,
      hover: baseColors.selectedBg,
      active: '#2a2b30',
      text: fontColors.primary,
      border: baseColors.border,
    },
    outline: {
      bg: 'transparent',
      hover: 'rgba(94, 105, 211, 0.1)',
      active: 'rgba(94, 105, 211, 0.2)',
      text: accentColors.primary,
      border: accentColors.primary,
    },
  },
} as const;

// Utility function to convert px to rem
export const pxToRem = (px: number) => `${px / 16}rem`;

// Pre-converted common values
export const remValues = {
  fontSizes: {
    '2xs': pxToRem(fontSizes['2xs']),
    xs: pxToRem(fontSizes.xs),
    sm: pxToRem(fontSizes.sm),
    md: pxToRem(fontSizes.md),
    lg: pxToRem(fontSizes.lg),
    xl: pxToRem(fontSizes.xl),
  },
  spacing: {
    xxs: pxToRem(spacing.xxs),
    xs: pxToRem(spacing.xs),
    sm: pxToRem(spacing.sm),
    md: pxToRem(spacing.md),
    lg: pxToRem(spacing.lg),
  },
  radii: {
    xs: pxToRem(radii.xs),
    sm: pxToRem(radii.sm),
    lg: pxToRem(radii.lg),
    xl: pxToRem(radii.xl),
  },
} as const;

// Type exports for TypeScript support
export type BaseColor = keyof typeof baseColors;
export type FontColor = keyof typeof fontColors;
export type AccentColor = keyof typeof accentColors;
export type FontSize = keyof typeof fontSizes;
export type Spacing = keyof typeof spacing;
export type Radii = keyof typeof radii;
